import { OsobaControllerService } from '@enauka/api';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OsobaDto, Rola, PowodBlokadyKonta } from '@enauka/shared/common-dto';
import { EventsService } from '@enauka/shared/infrastructure/events';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { SIGN_IN, SIGN_OUT } from './auth.events';
import { Credentials } from './Credentials';

export class AuthServiceConfig {
  api = "/api";
  userPrincipal = "userPrincipal";
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authenticated = new ReplaySubject<boolean>(1);
  private userPrincipal: BehaviorSubject<OsobaDto> = new BehaviorSubject<OsobaDto>(null);
  private userPrincipalUrl;
  private loginUrl;
  private logoutUrl;

  constructor(private config: AuthServiceConfig, private http: HttpClient, private eventsService: EventsService, private osobaControllerService: OsobaControllerService) {
    this.userPrincipalUrl = `${config.api}/${config.userPrincipal}`;
    this.loginUrl = `${config.api}/login`;
    this.logoutUrl = `${config.api}/logout`;
    this.updateUserPrincipal().subscribe();
  }

  updateUserPrincipal() {
    return this.http.get(this.userPrincipalUrl).pipe(
      tap(
        (osoba: OsobaDto) => {
          this.updateState(osoba);
        },
        (err) => this.updateState(null)
      )
    );
  }

  private updateState(osoba: OsobaDto) {
    this.userPrincipal.next(osoba as OsobaDto);
    this.authenticated.next(osoba !== null);
  }

  authenticate(credentials: Credentials) {
    return new Observable((observer) => {
      const body = new URLSearchParams();
      body.set('j_username', credentials.username);
      body.set('j_password', credentials.password);
      const headers = new HttpHeaders()
        .append('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8');

      return this.http.post(this.loginUrl, body.toString(), {
        headers: headers
      }).subscribe(
        (res) => {
          this.updateUserPrincipal().subscribe(
            () => observer.next(this.userPrincipal.getValue() !== null),
            () => observer.next(this.userPrincipal.getValue() !== null)
          );
          this.eventsService.emit(SIGN_IN);
        },
        (err) => {
          this.updateState(null);
          observer.error(err);
        });
    });
  }

  logout() {
    return this.http.post(this.logoutUrl, {})
      .pipe(
        mergeMap(() => this.updateUserPrincipal()),
        tap(() => {
          this.eventsService.emit(SIGN_OUT);
        })
      );
  }

  inicjujResetHasla(email: string): Observable<void> {
    const headers = new HttpHeaders()
      .append('Content-Type', 'text/plain');
    return this.http.post(`${this.config.api}/rejestracja/password/renew/init`, email, { headers }) as Observable<null>;
  }

  isAuthenticated$(): Observable<boolean> {
    return this.authenticated;
  }

  isInAnyRole(role: Rola[]): boolean {
    const userRoles = this.getUserRoles();
    return role.some(r => userRoles.includes(r));
  }

  isInRole(rola: Rola): boolean {
    return this.getUserRoles().includes(rola);
  }

  getUserPrincipal(): OsobaDto {
    return this.userPrincipal.getValue();
  }

  getUserPrincipal$(): BehaviorSubject<OsobaDto> {
    return this.userPrincipal;
  }

  getUserRoles(): Rola[] {
    const principal = this.getUserPrincipal();
    if (principal) {
      return principal.role;
    }
    return [];
  }

  getMainRole(): Rola {
    const principal = this.getUserPrincipal();
    if (principal) {
      return principal.rola;
    }
    return null;
  }

  getOpisPrzyczynyDezaktywacji(powod: PowodBlokadyKonta) : Observable<string> {
    return this.osobaControllerService.getOpisPowoduDezaktywacjiUsingGET(powod);
  }

  isAdmin(): boolean {
    return this.isInRole(Rola.ROLE_ADMIN);
  }

  isNauczyciel(): boolean {
    return this.isInRole(Rola.ROLE_NAUCZYCIEL);
  }

  isHelpDesk(): boolean {
    return this.isInRole(Rola.ROLE_HELP_DESK);
  }

  isUczen(): boolean {
    return this.isInRole(Rola.ROLE_UCZEN);
  }

  isStudent(): boolean {
    return this.isInRole(Rola.ROLE_STUDENT);
  }

  isRodzic(): boolean {
    return this.isInRole(Rola.ROLE_RODZIC);
  }

  isTester(): boolean {
    return this.isInRole(Rola.ROLE_TESTER);
  }

}
